import React, { useState } from 'react';
import {
  Card,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Typography,
  Upload,
  message,
  Empty,
  Spin,
  Breadcrumb,
  Alert,
  Tag,
  Popconfirm
} from 'antd';
import {
  FolderOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  UploadOutlined,
  DownloadOutlined,
  FolderAddOutlined,
  HomeOutlined,
  FileAddOutlined,
  EyeOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../config/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Folder {
  id: string;
  name: string;
  description?: string;
  parent_id?: string;
  document_count: number;
  subfolder_count: number;
  can_upload?: boolean;
  can_delete?: boolean;
  effective_can_delete?: boolean;
  created_at: string;
}

interface PortalDocument {
  id: string;
  name: string;
  description?: string;
  file_name: string;
  file_size: number;
  file_type: string;
  folder_id?: string;
  is_shared_with_all_sales: boolean;
  can_download?: boolean;
  effective_can_delete?: boolean;
  can_delete?: boolean;
  created_at: string;
}

const SalesPortal: React.FC = () => {
  const queryClient = useQueryClient();
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [folderStack, setFolderStack] = useState<Folder[]>([]);
  const [folderModalVisible, setFolderModalVisible] = useState(false);
  const [documentModalVisible, setDocumentModalVisible] = useState(false);
  const [folderForm] = Form.useForm();
  const [documentForm] = Form.useForm();

  // Fetch folders accessible by sales agent
  const { data: foldersData, isLoading: foldersLoading, error: foldersError } = useQuery(
    ['sales-portal-folders'],
    async () => {
      const response = await api.get('/api/sales-portal/folders');
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 30000, // 30 seconds
      onError: (error: any) => {
        console.error('Error fetching folders:', error);
        message.error(error.response?.data?.error || 'Failed to load folders');
      }
    }
  );

  // Fetch documents accessible by sales agent
  const { data: documentsData, isLoading: documentsLoading, error: documentsError } = useQuery(
    ['sales-portal-documents', selectedFolder],
    async () => {
      const params = new URLSearchParams();
      if (selectedFolder !== null && selectedFolder !== undefined) {
        params.append('folder_id', selectedFolder);
      } else {
        params.append('folder_id', 'null');
      }
      const response = await api.get(`/api/sales-portal/documents?${params.toString()}`);
      return response.data;
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 30000, // 30 seconds
      onError: (error: any) => {
        console.error('Error fetching documents:', error);
        message.error(error.response?.data?.error || 'Failed to load documents');
      }
    }
  );

  const folders: Folder[] = foldersData?.folders || [];
  const documents: PortalDocument[] = documentsData?.documents || [];

  // Filter folders by selected parent
  const currentFolders = folders.filter(f => {
    if (selectedFolder === null) {
      return !f.parent_id;
    }
    return f.parent_id === selectedFolder;
  });

  // Upload document mutation
  const uploadDocumentMutation = useMutation(
    async (formData: FormData) => {
      const response = await api.post('/api/sales-portal/documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Document uploaded successfully');
        queryClient.invalidateQueries(['sales-portal-documents']);
        queryClient.invalidateQueries(['sales-portal-folders']);
        // Force refetch with current folder selection
        queryClient.refetchQueries(['sales-portal-documents', selectedFolder]);
        queryClient.refetchQueries(['sales-portal-folders', selectedFolder]);
        setDocumentModalVisible(false);
        documentForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to upload document');
      }
    }
  );

  // Create folder mutation
  const createFolderMutation = useMutation(
    async (values: any) => {
      const response = await api.post('/api/sales-portal/folders', values);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Folder created successfully');
        queryClient.invalidateQueries(['sales-portal-folders']);
        // Force refetch with current folder selection
        queryClient.refetchQueries(['sales-portal-folders', selectedFolder]);
        setFolderModalVisible(false);
        folderForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to create folder');
      }
    }
  );

  // Delete folder mutation
  const deleteFolderMutation = useMutation(
    async (folderId: string) => {
      const response = await api.delete(`/api/sales-portal/folders/${folderId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Folder deleted successfully');
        queryClient.invalidateQueries(['sales-portal-folders']);
        queryClient.invalidateQueries(['sales-portal-documents']);
        queryClient.refetchQueries(['sales-portal-folders']);
        queryClient.refetchQueries(['sales-portal-documents', selectedFolder]);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to delete folder');
      }
    }
  );

  // Delete document mutation
  const deleteDocumentMutation = useMutation(
    async (documentId: string) => {
      const response = await api.delete(`/api/sales-portal/documents/${documentId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('Document deleted successfully');
        queryClient.invalidateQueries(['sales-portal-documents']);
        queryClient.refetchQueries(['sales-portal-documents', selectedFolder]);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.error || 'Failed to delete document');
      }
    }
  );

  // Download document
  const handleDownload = async (documentId: string, fileName: string) => {
    try {
      const response = await api.get(`/api/sales-portal/documents/${documentId}/download`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.setAttribute('download', fileName || 'download');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('Download started');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to download document');
    }
  };

  // Preview document
  const handlePreview = async (document: PortalDocument) => {
    try {
      const response = await api.get(`/api/sales-portal/documents/${document.id}/download`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: document.file_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);

      // Open in a new tab reliably
      const win = window.open(url, '_blank');
      if (!win) {
        // Popup blocked; fallback to creating a link
        const a = window.document.createElement('a');
        a.href = url;
        a.target = '_blank';
        window.document.body.appendChild(a);
        a.click();
        window.document.body.removeChild(a);
      }

      // Revoke later to allow the tab to load
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to preview document');
    }
  };

  const handleFolderClick = (folder: Folder) => {
    setFolderStack([...folderStack, folder]);
    setSelectedFolder(folder.id);
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      setSelectedFolder(null);
      setFolderStack([]);
    } else {
      const newStack = folderStack.slice(0, index + 1);
      setFolderStack(newStack);
      setSelectedFolder(newStack[newStack.length - 1].id);
    }
  };

  const handleCreateFolder = () => {
    folderForm.resetFields();
    setFolderModalVisible(true);
  };

  const handleUploadDocument = () => {
    documentForm.resetFields();
    documentForm.setFieldsValue({ folder_id: selectedFolder || null });
    setDocumentModalVisible(true);
  };

  const handleFolderSubmit = (values: any) => {
    createFolderMutation.mutate({
      ...values,
      parent_id: selectedFolder || null
    });
  };

  const handleDocumentSubmit = async (values: any) => {
    const formData = new FormData();
    formData.append('name', values.name);
    if (values.description) formData.append('description', values.description);
    // Handle folder_id - use form value, selectedFolder, or empty string for root level
    const folderId = values.folder_id || selectedFolder || null;
    if (folderId) {
      formData.append('folder_id', folderId);
    } else {
      // Explicitly set to empty string for root level uploads
      formData.append('folder_id', '');
    }

    const fileList = documentForm.getFieldValue('file')?.fileList;
    if (fileList && fileList.length > 0) {
      formData.append('file', fileList[0].originFileObj);
    } else {
      message.error('Please select a file to upload');
      return;
    }

    uploadDocumentMutation.mutate(formData);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string, size: number = 48) => {
    if (fileType.includes('pdf')) return <FilePdfOutlined style={{ fontSize: size, color: '#ff4d4f' }} />;
    if (fileType.includes('image')) return <FileImageOutlined style={{ fontSize: size, color: '#52c41a' }} />;
    if (fileType.includes('word') || fileType.includes('document')) return <FileWordOutlined style={{ fontSize: size, color: '#1890ff' }} />;
    if (fileType.includes('excel') || fileType.includes('spreadsheet')) return <FileExcelOutlined style={{ fontSize: size, color: '#52c41a' }} />;
    if (fileType.includes('text')) return <FileTextOutlined style={{ fontSize: size, color: '#faad14' }} />;
    return <FileOutlined style={{ fontSize: size, color: '#8c8c8c' }} />;
  };

  const canUpload = folderStack.length === 0 || (folderStack[folderStack.length - 1]?.can_upload !== false);

  return (
    <div style={{ margin: '24px', background: '#f5f5f5', minHeight: 'calc(100vh - 112px)' }}>
      <Card
        style={{ marginBottom: 16 }}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Space>
            <FolderOutlined style={{ fontSize: 24 }} />
            <Title level={4} style={{ margin: 0 }}>Sales Portal</Title>
          </Space>
          {canUpload && (
            <Space>
              <Button icon={<FolderAddOutlined />} onClick={handleCreateFolder}>
                New Folder
              </Button>
              <Button type="primary" icon={<FileAddOutlined />} onClick={handleUploadDocument}>
                Upload
              </Button>
            </Space>
          )}
        </Space>
      </Card>

      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* Breadcrumb */}
          <Card size="small" style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
            <Breadcrumb
              separator={<span style={{ color: '#d9d9d9', margin: '0 8px' }}>/</span>}
              style={{ fontSize: '14px' }}
            >
              <Breadcrumb.Item>
                <button
                  type="button"
                  onClick={() => handleBreadcrumbClick(-1)}
                  style={{ 
                    cursor: 'pointer',
                    color: folderStack.length === 0 ? '#1890ff' : '#595959',
                    fontWeight: folderStack.length === 0 ? 500 : 400,
                    textDecoration: 'none',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#1890ff'}
                  onMouseLeave={(e) => e.currentTarget.style.color = folderStack.length === 0 ? '#1890ff' : '#595959'}
                >
                  <HomeOutlined style={{ marginRight: 4 }} />
                  Home
                </button>
              </Breadcrumb.Item>
              {folderStack.map((folder, index) => (
                <Breadcrumb.Item key={folder.id}>
                  <button
                    type="button"
                    onClick={() => handleBreadcrumbClick(index)}
                    style={{ 
                      cursor: 'pointer',
                      color: index === folderStack.length - 1 ? '#1890ff' : '#595959',
                      fontWeight: index === folderStack.length - 1 ? 500 : 400,
                      textDecoration: 'none',
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                    }}
                    onMouseEnter={(e) => {
                      if (index !== folderStack.length - 1) {
                        e.currentTarget.style.color = '#1890ff';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (index !== folderStack.length - 1) {
                        e.currentTarget.style.color = '#595959';
                      }
                    }}
                  >
                    {index === folderStack.length - 1 && <FolderOutlined style={{ marginRight: 4 }} />}
                    {folder.name}
                  </button>
                </Breadcrumb.Item>
              ))}
            </Breadcrumb>
          </Card>

          {foldersError || documentsError ? (
            <Alert
              message="Error Loading Data"
              description={foldersError?.response?.data?.error || documentsError?.response?.data?.error || 'Failed to load folders or documents. Please check your permissions.'}
              type="error"
              showIcon
            />
          ) : (foldersLoading || documentsLoading) ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
            </div>
          ) : currentFolders.length === 0 && documents.length === 0 ? (
            <Empty 
              description="No folders or documents available. Contact your administrator to get access to folders and documents." 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <div style={{ background: '#fff', borderRadius: 4 }}>
              {/* Table Header */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '40px 2fr 1fr 120px 180px',
                padding: '12px 16px',
                background: '#fafafa',
                borderBottom: '1px solid #f0f0f0',
                fontWeight: 500,
                fontSize: 14
              }}>
                <div></div>
                <div>Name</div>
                <div>Size</div>
                <div>Type</div>
                <div style={{ textAlign: 'right' }}>Actions</div>
              </div>

              {/* Folders */}
              {currentFolders.map(folder => (
                <div
                  key={folder.id}
                  onClick={() => handleFolderClick(folder)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '40px 2fr 1fr 120px 180px',
                    padding: '12px 16px',
                    borderBottom: '1px solid #f0f0f0',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fff';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FolderOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Text strong>{folder.name}</Text>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', color: '#8c8c8c', fontSize: 13 }}>
                    {folder.document_count} files, {folder.subfolder_count} folders
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', color: '#8c8c8c', fontSize: 13 }}>
                    Folder
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Space>
                      <Button
                        type="link"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFolderClick(folder);
                        }}
                      >
                        Open
                      </Button>
                      {(folder.can_delete || folder.effective_can_delete) && (
                        <Popconfirm
                          title="Delete folder?"
                          description="This will also delete all subfolders and documents inside."
                          onConfirm={(e) => {
                            e?.stopPropagation();
                            deleteFolderMutation.mutate(folder.id);
                          }}
                          onCancel={(e) => e?.stopPropagation()}
                        >
                          <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Delete
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  </div>
                </div>
              ))}

              {/* Documents */}
              {documents.map(doc => (
                <div
                  key={doc.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '40px 2fr 1fr 120px 180px',
                    padding: '12px 16px',
                    borderBottom: '1px solid #f0f0f0',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f5f5f5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#fff';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    {getFileIcon(doc.file_type, 20)}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'center' }}>
                    <Space>
                      <Text strong>{doc.name}</Text>
                      {doc.is_shared_with_all_sales && (
                        <Tag color="green">Shared</Tag>
                      )}
                    </Space>
                    {doc.description && (
                      <Text type="secondary" style={{ fontSize: 12, marginTop: 2 }}>
                        {doc.description}
                      </Text>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', color: '#8c8c8c', fontSize: 13 }}>
                    {formatFileSize(doc.file_size)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', color: '#8c8c8c', fontSize: 13 }}>
                    {doc.file_type.split('/')[1]?.toUpperCase() || 'FILE'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Space>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePreview(doc);
                        }}
                      >
                        Preview
                      </Button>
                      <Button
                        type="link"
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(doc.id, doc.file_name);
                        }}
                        disabled={doc.can_download === false}
                      >
                        Download
                      </Button>
                      {(doc.effective_can_delete || doc.can_delete) && (
                        <Popconfirm
                          title="Delete document?"
                          description="This action cannot be undone."
                          onConfirm={(e) => {
                            e?.stopPropagation();
                            deleteDocumentMutation.mutate(doc.id);
                          }}
                          onCancel={(e) => e?.stopPropagation()}
                        >
                          <Button
                            type="link"
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Delete
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Space>
      </Card>

      {/* Create Folder Modal */}
      <Modal
        title="Create Folder"
        open={folderModalVisible}
        onCancel={() => {
          setFolderModalVisible(false);
          folderForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={folderForm}
          layout="vertical"
          onFinish={handleFolderSubmit}
        >
          <Form.Item
            name="name"
            label="Folder Name"
            rules={[{ required: true, message: 'Folder name is required' }]}
          >
            <Input placeholder="Enter folder name" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Enter folder description" />
          </Form.Item>
          <Alert
            message={`Folder will be created in: ${selectedFolder ? (folderStack.find(f => f.id === selectedFolder)?.name || 'Current Location') : 'Root'}`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={createFolderMutation.isLoading}>
                Create
              </Button>
              <Button onClick={() => {
                setFolderModalVisible(false);
                folderForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Upload Document Modal */}
      <Modal
        title="Upload Document"
        open={documentModalVisible}
        onCancel={() => {
          setDocumentModalVisible(false);
          documentForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={documentForm}
          layout="vertical"
          onFinish={handleDocumentSubmit}
        >
          <Form.Item
            name="file"
            label="File"
            rules={[{ required: true, message: 'Please select a file' }]}
          >
            <Upload
              maxCount={1}
              beforeUpload={() => false}
            >
              <Button icon={<UploadOutlined />}>Select File</Button>
            </Upload>
          </Form.Item>
          <Form.Item
            name="name"
            label="Document Name"
            rules={[{ required: true, message: 'Document name is required' }]}
          >
            <Input placeholder="Enter document name" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={3} placeholder="Enter document description" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={uploadDocumentMutation.isLoading}>
                Upload
              </Button>
              <Button onClick={() => {
                setDocumentModalVisible(false);
                documentForm.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SalesPortal;
